import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { getOrder } from '../api';
import axios from 'axios';
import './OrderSuccess.css';

export default function OrderSuccess() {
  const { orderNumber } = useParams();
  const [searchParams]  = useSearchParams();
  const navigate        = useNavigate();

  const [order, setOrder]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [status, setStatus]       = useState('verifying');
  const [attempt, setAttempt]     = useState(0);
  const [countdown, setCountdown] = useState(3);
  const timerRef = useRef(null);

  const MAX_RETRIES = 8;
  const RETRY_DELAY = 3000;

  useEffect(() => {
    const cfOrderId = searchParams.get('cf_order_id');

    if (!cfOrderId) {
      getOrder(orderNumber)
        .then(r => { setOrder(r.data); setStatus('success'); })
        .catch(() => setStatus('failed'))
        .finally(() => setLoading(false));
      return;
    }

    // Wait 2s before first check — Cashfree needs time to update status
    const t = setTimeout(() => setAttempt(1), 2000);
    return () => clearTimeout(t);
  }, [orderNumber, searchParams]);

  useEffect(() => {
    if (attempt === 0) return;
    const cfOrderId = searchParams.get('cf_order_id');
    if (!cfOrderId) return;

    async function check() {
      try {
        const { data } = await axios.get(`/api/orders/verify/${orderNumber}`);
        setOrder(data);

        // COD orders prepay only the COD charge, tracked in codFeeStatus
        const onlineStatus = data.paymentMethod === 'cod' ? data.codFeeStatus : data.paymentStatus;

        if (onlineStatus === 'paid') {
          setStatus('success');
          setLoading(false);
          return;
        }

        if (onlineStatus === 'failed') {
          setStatus('abandoned');
          setLoading(false);
          return;
        }

        if (attempt < MAX_RETRIES) {
          setCountdown(3);
          timerRef.current = setInterval(() => {
            setCountdown(c => {
              if (c <= 1) { clearInterval(timerRef.current); return 0; }
              return c - 1;
            });
          }, 1000);
          setTimeout(() => setAttempt(a => a + 1), RETRY_DELAY);
        } else {
          setStatus('abandoned');
          setLoading(false);
        }
      } catch {
        if (attempt < MAX_RETRIES) {
          setTimeout(() => setAttempt(a => a + 1), RETRY_DELAY);
        } else {
          setStatus('failed');
          setLoading(false);
        }
      }
    }

    check();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [attempt, orderNumber, searchParams]);

  if (loading) {
    return (
      <div className="success-page">
        <div className="success-card">
          <div className="verify-spinner">
            <div className="spinner-ring" />
          </div>
          <h2>Verifying your payment…</h2>
          <p style={{ fontSize: 14, color: '#6b7280', marginTop: 8 }}>
            Please wait, do not close this page.
          </p>
          {attempt > 1 && (
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 8 }}>
              Checking with payment gateway… ({attempt}/{MAX_RETRIES})
              {countdown > 0 && ` · next check in ${countdown}s`}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (status === 'abandoned') {
    return (
      <div className="success-page">
        <div className="success-card failed">
          <div className="success-icon">⚠️</div>
          <h1>Payment Incomplete</h1>
          <p className="success-msg">
            You left the payment page before completing the transaction.
            Your order <strong>#{orderNumber}</strong> has not been confirmed.
          </p>
          <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 24 }}>
            If money was deducted from your account, it will be refunded within 5–7 business days.
          </p>
          <div className="success-actions">
            <button className="btn-continue" onClick={() => navigate('/cart')}>Return to Cart</button>
            <button className="btn-home" onClick={() => navigate('/')}>Go Home</button>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div className="success-page">
        <div className="success-card failed">
          <div className="success-icon">❌</div>
          <h1>Payment Failed</h1>
          <p className="success-msg">Your payment could not be processed. Please try again.</p>
          <div className="success-actions">
            <button className="btn-continue" onClick={() => navigate('/cart')}>Return to Cart</button>
            <button className="btn-home" onClick={() => navigate('/')}>Go Home</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="success-page">
      <div className="success-card">
        <div className="success-icon">✅</div>
        <h1>Order Confirmed!</h1>
        <p className="success-order-num">Order #{orderNumber}</p>
        <p className="success-msg">
          Thank you for your purchase! We'll start making your order and send you an update once it ships.
        </p>
        {order && (
          <div className="order-details">
            <div className="detail-row">
              <span>Payment</span>
              <span>{order.paymentStatus === 'paid' ? '✅ Paid' : order.paymentMethod === 'cod' ? (order.codFeeStatus === 'paid' ? `💵 Cash on Delivery (₹${order.codFee} COD charges paid)` : '💵 Cash on Delivery') : order.paymentStatus}</span>
            </div>
            <div className="detail-row">
              <span>Total</span>
              <span>₹{order.total?.toLocaleString('en-IN')}</span>
            </div>
            <div className="detail-row">
              <span>Delivering to</span>
              <span>{order.shippingAddress?.city}, {order.shippingAddress?.state}</span>
            </div>
            <div className="detail-row">
              <span>Estimated dispatch</span>
              <span>3–7 business days</span>
            </div>
          </div>
        )}
        <div className="success-actions">
          <Link to="/products" className="btn-continue">Continue Shopping</Link>
          <button className="btn-home" onClick={() => navigate(`/track-order?order=${orderNumber}`)}>Track Order</button>
        </div>
      </div>
    </div>
  );
}
